import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import Icon from '../../components/AppIcon';
import SettingsSection from './components/SettingsSection';
import SettingItem from './components/SettingItem';
import DataManagementCard from './components/DataManagementCard';
import HelpSupportSection from './components/HelpSupportSection';
import PrivacyInfoCard from './components/PrivacyInfoCard';
import EncryptionManagement from './components/EncryptionManagement';
import { useAuth } from '../../contexts/AuthContext';
import { hasFeatureAccess, OFFICER_FEATURES } from '../../utils/rolePermissions';
import RestrictedFeature from '../../components/RestrictedFeature';
import PermissionWarning from '../../components/PermissionWarning';
import notificationService from '../../services/notificationService';
import { useNavigate } from 'react-router-dom';
import { useAccessibility } from '../../contexts/AccessibilityContext';
import sessionService from '../../services/sessionService';
import privacyService from '../../services/privacyService';
import UserProfileHeader from '../../components/navigation/UserProfileHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';




const SettingsAndPrivacy = () => {
  const { user, resendVerificationEmail, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const { settings: accessibilitySettings, updateSetting: updateAccessibilitySetting } = useAccessibility();
  const { userProfile } = useAuth();
  const [settings, setSettings] = useState({
    dailyReminder: true,
    reminderTime: '09:00',
    emailRemindersEnabled: true,
    emailReminderFrequency: 'daily',
    emailReminderDays: [1, 2, 3, 4, 5, 6, 7],
    checkinReminderFrequency: 'daily',
    checkinReminderDays: [1, 2, 3, 4, 5, 6, 7],
    checkinReminderTimeSlots: ['morning'],
    caregiverAlertEnabled: true,
    caregiverAlertFrequency: 'immediate',
    caregiverAlertQuietHours: { enabled: false, start: '22:00', end: '07:00' },
    supportEmailEnabled: true,
    supportEmailFrequency: 'immediate',
    textSize: 16,
    highContrast: false,
    screenReader: false,
    buttonSize: 'default',
    colorScheme: 'auto',
    voiceQuality: 'medium',
    language: 'en',
    dateFormat: 'MM/DD/YYYY'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [privacySettings, setPrivacySettings] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  // Check if user is a MyBodyToday officer (owner/admin)
  const isOfficer = userProfile?.email?.toLowerCase()?.includes('@mybodytoday.') || 
                    userProfile?.email?.toLowerCase() === 'owner@mybodytoday.com' ||
                    userProfile?.email?.toLowerCase() === 'admin@mybodytoday.com';

  // Check access for officer-exclusive features
  const hasBusinessIntelligenceAccess = hasFeatureAccess(userProfile, OFFICER_FEATURES?.BUSINESS_INTELLIGENCE);
  const hasUserEngagementAccess = hasFeatureAccess(userProfile, OFFICER_FEATURES?.USER_ENGAGEMENT_ANALYTICS);
  const hasConversionFunnelAccess = hasFeatureAccess(userProfile, OFFICER_FEATURES?.CONVERSION_FUNNEL_ANALYTICS);
  const hasAuditTrailAccess = hasFeatureAccess(userProfile, OFFICER_FEATURES?.AUDIT_TRAIL);

  useEffect(() => {
    if (user?.id) {
      loadUserSettings();
      loadSessions();
      loadPrivacySettings();
    }
  }, [user]);

  const loadUserSettings = async () => {
    try {
      setLoading(true);
      const userSettings = await notificationService?.getUserSettings(user?.id);
      
      if (userSettings) {
        setSettings((prev) => ({
          ...prev,
          dailyReminder: userSettings?.symptomReminders ?? true,
          reminderTime: userSettings?.reminderTime || '09:00',
          emailRemindersEnabled: userSettings?.emailRemindersEnabled ?? true,
          emailReminderFrequency: userSettings?.emailReminderFrequency || 'daily',
          emailReminderDays: userSettings?.emailReminderDays || [1, 2, 3, 4, 5, 6, 7],
          checkinReminderFrequency: userSettings?.checkinReminderFrequency || 'daily',
          checkinReminderDays: userSettings?.checkinReminderDays || [1, 2, 3, 4, 5, 6, 7],
          checkinReminderTimeSlots: userSettings?.checkinReminderTimeSlots || ['morning'],
          caregiverAlertEnabled: userSettings?.caregiverAlertEnabled ?? true,
          caregiverAlertFrequency: userSettings?.caregiverAlertFrequency || 'immediate',
          caregiverAlertQuietHours: userSettings?.caregiverAlertQuietHours || { enabled: false, start: '22:00', end: '07:00' },
          supportEmailEnabled: userSettings?.supportEmailEnabled ?? true,
          supportEmailFrequency: userSettings?.supportEmailFrequency || 'immediate'
        }));
      }
    } catch (error) {
      console.error('Failed to load user settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    try {
      const { data } = await sessionService?.getActiveSessions();
      if (data) setSessions(data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadPrivacySettings = async () => {
    try {
      const { data } = await privacyService?.getPrivacySettings();
      if (data) setPrivacySettings(data);
    } catch (error) {
      console.error('Failed to load privacy settings:', error);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      await sessionService?.revokeSession(sessionId);
      loadSessions();
    } catch (error) {
      console.error('Failed to revoke session:', error);
    }
  };

  const handlePrivacySettingChange = async (key, value) => {
    try {
      const updates = { [key]: value };
      await privacyService?.updatePrivacySettings(updates);
      setPrivacySettings(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Failed to update privacy setting:', error);
    }
  };

  const handleSettingChange = async (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));

    // Save notification-related settings to database
    if (['dailyReminder', 'reminderTime', 'emailRemindersEnabled', 'emailReminderFrequency', 'emailReminderDays', 'checkinReminderFrequency', 'checkinReminderDays', 'checkinReminderTimeSlots', 'caregiverAlertEnabled', 'caregiverAlertFrequency', 'caregiverAlertQuietHours', 'supportEmailEnabled', 'supportEmailFrequency']?.includes(key)) {
      try {
        setSaving(true);
        const updates = {};

        if (key === 'dailyReminder') updates.symptomReminders = value;
        if (key === 'reminderTime') updates.reminderTime = value;
        if (key === 'emailRemindersEnabled') updates.emailRemindersEnabled = value;
        if (key === 'emailReminderFrequency') updates.emailReminderFrequency = value;
        if (key === 'emailReminderDays') updates.emailReminderDays = value;
        if (key === 'checkinReminderFrequency') updates.checkinReminderFrequency = value;
        if (key === 'checkinReminderDays') updates.checkinReminderDays = value;
        if (key === 'checkinReminderTimeSlots') updates.checkinReminderTimeSlots = value;
        if (key === 'caregiverAlertEnabled') updates.caregiverAlertEnabled = value;
        if (key === 'caregiverAlertFrequency') updates.caregiverAlertFrequency = value;
        if (key === 'caregiverAlertQuietHours') updates.caregiverAlertQuietHours = value;
        if (key === 'supportEmailEnabled') updates.supportEmailEnabled = value;
        if (key === 'supportEmailFrequency') updates.supportEmailFrequency = value;

        await notificationService?.updateUserSettings(user?.id, updates);
      } catch (error) {
        console.error('Failed to save setting:', error);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleTestEmail = async () => {
    try {
      setTestEmailSending(true);
      setTestEmailStatus(null);
      
      await notificationService?.testSendReminder(user?.id);
      
      setTestEmailStatus({ type: 'success', message: 'Test email sent successfully! Check your inbox.' });
      setTimeout(() => setTestEmailStatus(null), 5000);
    } catch (error) {
      console.error('Failed to send test email:', error);
      setTestEmailStatus({ type: 'error', message: 'Failed to send test email. Please try again.' });
      setTimeout(() => setTestEmailStatus(null), 5000);
    } finally {
      setTestEmailSending(false);
    }
  };

  const handleClearData = () => {
    console.log('All data cleared');
  };

  const handleExportData = () => {
    const exportData = {
      settings,
      exportDate: new Date()?.toISOString(),
      version: '1.0.0'
    };
    console.log('Data exported:', exportData);
  };

  const handleExportJSON = async () => {
    try {
      setExportLoading(true);
      const exportData = {
        settings,
        user: {
          email: user?.email,
          id: user?.id
        },
        exportDate: new Date()?.toISOString(),
        version: '1.0.0'
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mybodytoday-data-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);
      
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 5000);
    } catch (error) {
      console.error('Failed to export JSON:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const csvData = `Setting,Value\n${Object.entries(settings)?.map(([key, value]) => `${key},${value}`)?.join('\n')}`;
      
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mybodytoday-data-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);
      
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 5000);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await resendVerificationEmail(user?.email);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error) {
      console.error('Failed to resend verification email:', error);
    } finally {
      setResendLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Password is required to delete your account');
      return;
    }

    setDeleteLoading(true);
    setDeleteError('');

    try {
      // First verify password by attempting to sign in
      const { error: signInError } = await supabase?.auth?.signInWithPassword({
        email: user?.email,
        password: deletePassword
      });

      if (signInError) {
        setDeleteError('Incorrect password. Please try again.');
        setDeleteLoading(false);
        return;
      }

      // Password verified - proceed with account deletion
      logger?.audit('ACCOUNT_DELETION_INITIATED', user?.id, { email: user?.email });

      // Call deleteAccount from AuthContext
      const { error: deleteError } = await deleteAccount();

      if (deleteError) {
        setDeleteError(deleteError?.message || 'Failed to delete account. Please try again.');
        logger?.error('Account deletion failed', deleteError, { userId: user?.id });
      } else {
        // Success - user will be signed out automatically by AuthContext
        logger?.audit('ACCOUNT_DELETED', user?.id, { email: user?.email });
        // Navigation to sign-in will happen automatically after sign out
      }
    } catch (error) {
      console.error('Delete account error:', error);
      setDeleteError('An unexpected error occurred. Please try again.');
      logger?.error('Delete account exception', error, { userId: user?.id });
    } finally {
      setDeleteLoading(false);
    }
  };

  const isEmailUnverified = user && !user?.email_confirmed_at;

  const voiceQualityOptions = [
    { value: 'low', label: 'Low Quality (Smaller files)' },
    { value: 'medium', label: 'Medium Quality' },
    { value: 'high', label: 'High Quality (Larger files)' }
  ];

  const buttonSizeOptions = [
    { value: 'small', label: 'Small' },
    { value: 'default', label: 'Default' },
    { value: 'large', label: 'Large' }
  ];

  const colorSchemeOptions = [
    { value: 'auto', label: 'Auto (System)' },
    { value: 'light', label: 'Light Mode' },
    { value: 'dark', label: 'Dark Mode' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (UK)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' }
  ];

  const voiceLanguageOptions = [
    { value: 'en-US', label: '🇺🇸 English (US)', flag: '🇺🇸' },
    { value: 'en-GB', label: '🇬🇧 English (UK)', flag: '🇬🇧' },
    { value: 'fr-FR', label: '🇫🇷 French', flag: '🇫🇷' },
    { value: 'nl-NL', label: '🇳🇱 Dutch', flag: '🇳🇱' },
    { value: 'de-DE', label: '🇩🇪 German', flag: '🇩🇪' },
    { value: 'da-DK', label: '🇩🇰 Danish', flag: '🇩🇰' },
    { value: 'sv-SE', label: '🇸🇪 Swedish', flag: '🇸🇪' },
    { value: 'es-ES', label: '🇪🇸 Spanish', flag: '🇪🇸' },
    { value: 'it-IT', label: '🇮🇹 Italian', flag: '🇮🇹' },
    { value: 'pt-PT', label: '🇵🇹 Portuguese', flag: '🇵🇹' },
    { value: 'nb-NO', label: '🇳🇴 Norwegian', flag: '🇳🇴' },
    { value: 'fi-FI', label: '🇫🇮 Finnish', flag: '🇫🇮' }
  ];

  const checkinFrequencyOptions = [
    { value: 'never', label: 'Never' },
    { value: 'daily', label: 'Daily' },
    { value: 'every_other_day', label: 'Every Other Day' },
    { value: 'twice_weekly', label: 'Twice Weekly' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'custom', label: 'Custom Days' }
  ];

  const timeSlotOptions = [
    { value: 'morning', label: 'Morning (9:00 AM)' },
    { value: 'afternoon', label: 'Afternoon (2:00 PM)' },
    { value: 'evening', label: 'Evening (7:00 PM)' }
  ];

  const caregiverAlertFrequencyOptions = [
    { value: 'immediate', label: 'Immediate (Real-time)' },
    { value: 'hourly_digest', label: 'Hourly Digest' },
    { value: 'daily_digest', label: 'Daily Digest' },
    { value: 'critical_only', label: 'Critical Only' }
  ];

  const supportEmailFrequencyOptions = [
    { value: 'immediate', label: 'Immediate' },
    { value: 'daily_digest', label: 'Daily Digest' },
    { value: 'weekly_digest', label: 'Weekly Digest' }
  ];

  const emailFrequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'custom', label: 'Custom Days' }
  ];

  const dayOptions = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 7, label: 'Sunday' }
  ];

  const toggleDay = (day) => {
    const currentDays = settings?.emailReminderDays || [];
    const newDays = currentDays?.includes(day)
      ? currentDays?.filter(d => d !== day)
      : [...currentDays, day]?.sort((a, b) => a - b);
    handleSettingChange('emailReminderDays', newDays);
  };

  return (
    <>
      <Helmet>
        <title>Settings & Privacy - MyBodyToday</title>
        <meta name="description" content="Manage your account settings and privacy preferences" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 pb-24">
        <BottomTabNavigation />
        <UserProfileHeader />
        
        {/* Header with Close Button */}
        {isEmailUnverified && (
          <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex items-center justify-center w-12 h-12 bg-warning/20 rounded-lg flex-shrink-0">
                  <Icon name="AlertTriangle" size={24} color="var(--color-warning)" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-heading font-semibold text-warning-foreground mb-2">
                    Email Verification Required
                  </h3>
                  <p className="text-sm sm:text-base text-warning-foreground/80 mb-4">
                    Your email address <strong>{user?.email}</strong> has not been verified yet. Please check your inbox and spam folder for the verification link to access all features.
                  </p>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    {resendSuccess ? (
                      <div className="flex items-center gap-2 text-success">
                        <Icon name="CheckCircle" size={18} color="var(--color-success)" />
                        <span className="text-sm font-medium">Verification email sent successfully! Check your inbox.</span>
                      </div>
                    ) : (
                      <Button
                        variant="warning"
                        size="default"
                        onClick={handleResendVerification}
                        loading={resendLoading}
                        iconName="Mail"
                      >
                        Resend Verification Email
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="max-w-4xl mx-auto space-y-6">

            {/* Brand Header */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-4">
                <img
                  src="/assets/images/MyBodyToday_logo_design2-removebg-preview1-1770673381636.png"
                  alt="MyBodyToday Logo"
                  className="w-12 h-12 object-contain flex-shrink-0"
                />
                <div>
                  <h2 className="text-xl font-bold text-gray-900">MyBodyToday&#8482;</h2>
                  <p className="text-sm text-teal-600 font-medium italic">Track small changes before they become big ones&#8482;</p>
                </div>
              </div>
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                  <Icon name="Globe" size={14} className="text-blue-600" />
                  <span className="text-xs font-semibold text-blue-700">GDPR Compliant</span>
                </div>
                <div className="flex items-center gap-1.5 bg-teal-50 border border-teal-200 rounded-lg px-3 py-1.5">
                  <Icon name="Lock" size={14} className="text-teal-600" />
                  <span className="text-xs font-semibold text-teal-700">AES-256 Encrypted</span>
                </div>
                <div className="flex items-center gap-1.5 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-1.5">
                  <Icon name="Award" size={14} className="text-yellow-600" />
                  <span className="text-xs font-semibold text-yellow-700">ISO 27001 Aligned</span>
                </div>
                <div className="flex items-center gap-1.5 bg-purple-50 border border-purple-200 rounded-lg px-3 py-1.5">
                  <Icon name="Shield" size={14} className="text-purple-600" />
                  <span className="text-xs font-semibold text-purple-700">SOC 2 Aligned</span>
                </div>
              </div>
            </div>

            {/* Account & Subscription Settings */}
            <SettingsSection
              title="Account & Subscription"
              description="Manage your plan and usage limits"
              icon="CreditCard"
              collapsible={true}
              defaultOpen={true}
            >
              <SettingItem
                icon="TrendingUp"
                label="Usage Limits"
                description="View your feature usage and plan limits"
                type="button"
                buttonText="View Usage"
                onButtonClick={() => navigate('/usage-limits-dashboard')}
                value={null}
                onChange={() => {}}
                min={0}
                max={100}
              />
              <SettingItem
                icon="CreditCard"
                label="Subscription Management"
                description="Manage your subscription plan and billing"
                type="button"
                buttonText="Manage Plan"
                onButtonClick={() => navigate('/subscription-management')}
                value={null}
                onChange={() => {}}
                min={0}
                max={100}
              />
            </SettingsSection>

            <SettingsSection title="Security & Encryption" icon="Shield" description="Manage encryption and security features">
              <EncryptionManagement onEncryptionEnabled={loadPrivacySettings} />
            </SettingsSection>

            {/* Accessibility Settings */}
            <SettingsSection title="Accessibility" description="Adjust accessibility features" icon="Eye">
              <SettingItem
                icon="Type"
                label="Large Text Mode"
                description="Increase text size throughout the app for better readability"
                type="toggle"
                value={accessibilitySettings?.largeText}
                onChange={(value) => updateAccessibilitySetting('largeText', value)}
              />
              <SettingItem
                icon="Contrast"
                label="High Contrast Mode"
                description="Enhance color contrast for improved visibility"
                type="toggle"
                value={accessibilitySettings?.highContrast}
                onChange={(value) => updateAccessibilitySetting('highContrast', value)}
              />
              <SettingItem
                icon="Zap"
                label="Reduced Motion"
                description="Minimize animations and transitions"
                type="toggle"
                value={accessibilitySettings?.reducedMotion}
                onChange={(value) => updateAccessibilitySetting('reducedMotion', value)}
              />
              <SettingItem
                icon="Volume2"
                label="Screen Reader Optimized"
                description="Optimize interface for screen reader users"
                type="toggle"
                value={accessibilitySettings?.screenReaderOptimized}
                onChange={(value) => updateAccessibilitySetting('screenReaderOptimized', value)}
              />
            </SettingsSection>

            <SettingsSection
              title="Display & Accessibility"
              description="Adjust visual settings for better readability"
              icon="Eye"
              collapsible={true}
              defaultOpen={true}
            >
              <SettingItem
                icon="Type"
                label="Text Size"
                description="Adjust the size of text throughout the app"
                type="slider"
                value={settings?.textSize}
                onChange={(value) => handleSettingChange('textSize', value)}
                min={12}
                max={24}
                step={1}
                unit="px"
                preview={true}
              />
              
              <SettingItem
                icon="Contrast"
                label="High Contrast Mode"
                description="Increase contrast for better visibility"
                type="toggle"
                value={settings?.highContrast}
                onChange={(value) => handleSettingChange('highContrast', value)}
              />
              
              <SettingItem
                icon="Volume2"
                label="Screen Reader Support"
                description="Enable enhanced screen reader compatibility"
                type="toggle"
                value={settings?.screenReader}
                onChange={(value) => handleSettingChange('screenReader', value)}
              />
              
              <SettingItem
                icon="MousePointer"
                label="Button Size"
                description="Choose button size for easier interaction"
                type="select"
                value={settings?.buttonSize}
                onChange={(value) => handleSettingChange('buttonSize', value)}
                options={buttonSizeOptions}
              />
              
              <SettingItem
                icon="Palette"
                label="Color Scheme"
                description="Choose your preferred color theme"
                type="select"
                value={settings?.colorScheme}
                onChange={(value) => handleSettingChange('colorScheme', value)}
                options={colorSchemeOptions}
              />
            </SettingsSection>

            <SettingsSection
              title="Voice Notes"
              description="Configure voice recording settings"
              icon="Mic"
              collapsible={true}
              defaultOpen={false}
            >
              <SettingItem
                icon="Music"
                label="Recording Quality"
                description="Higher quality means larger file sizes"
                type="select"
                value={settings?.voiceQuality}
                onChange={(value) => handleSettingChange('voiceQuality', value)}
                options={voiceQualityOptions}
              />
            </SettingsSection>

            <SettingsSection
              title="Language & Format"
              description="Customize language and date preferences"
              icon="Globe"
              collapsible={true}
              defaultOpen={false}
            >
              <SettingItem
                icon="Languages"
                label="Language"
                description="Choose your preferred language"
                type="select"
                value={settings?.language}
                onChange={(value) => handleSettingChange('language', value)}
                options={languageOptions}
              />
              
              <SettingItem
                icon="Calendar"
                label="Date Format"
                description="Select how dates are displayed"
                type="select"
                value={settings?.dateFormat}
                onChange={(value) => handleSettingChange('dateFormat', value)}
                options={dateFormatOptions}
              />
            </SettingsSection>

            <SettingsSection
              title="Notifications"
              description="Manage daily check-in reminders"
              icon="Bell"
              collapsible={true}
              defaultOpen={false}
            >
              <SettingItem
                icon="Bell"
                label="Daily Check-in Reminder"
                description="Get reminded to log your symptoms"
                type="toggle"
                value={settings?.dailyReminder}
                onChange={(value) => handleSettingChange('dailyReminder', value)}
              />
              
              {settings?.dailyReminder && (
                <div className="pl-0 sm:pl-4 space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Reminder Frequency
                    </label>
                    <select
                      value={settings?.checkinReminderFrequency}
                      onChange={(e) => handleSettingChange('checkinReminderFrequency', e?.target?.value)}
                      className="
                        w-full px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-border bg-background
                        text-foreground text-sm sm:text-base
                        focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring
                        transition-smooth cursor-pointer
                      "
                    >
                      {checkinFrequencyOptions?.map((option) => (
                        <option key={option?.value} value={option?.value}>
                          {option?.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {settings?.checkinReminderFrequency === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Select Days
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {dayOptions?.map((day) => (
                          <button
                            key={day?.value}
                            onClick={() => {
                              const newDays = settings?.checkinReminderDays?.includes(day?.value)
                                ? settings?.checkinReminderDays?.filter(d => d !== day?.value)
                                : [...(settings?.checkinReminderDays || []), day?.value];
                              handleSettingChange('checkinReminderDays', newDays);
                            }}
                            className={`
                              px-3 py-2 rounded-lg text-sm font-medium transition-smooth
                              focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
                              ${settings?.checkinReminderDays?.includes(day?.value)
                                ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                              }
                            `}
                          >
                            {day?.label?.substring(0, 3)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Preferred Time Slots
                    </label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Choose when you'd like to receive reminders
                    </p>
                    <div className="space-y-2">
                      {timeSlotOptions?.map((slot) => (
                        <label key={slot?.value} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-smooth cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings?.checkinReminderTimeSlots?.includes(slot?.value)}
                            onChange={(e) => {
                              const newSlots = e?.target?.checked
                                ? [...(settings?.checkinReminderTimeSlots || []), slot?.value]
                                : settings?.checkinReminderTimeSlots?.filter(s => s !== slot?.value);
                              handleSettingChange('checkinReminderTimeSlots', newSlots);
                            }}
                            className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-ring"
                          />
                          <span className="text-sm text-foreground">{slot?.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Reminder Time
                    </label>
                    <input
                      type="time"
                      value={settings?.reminderTime}
                      onChange={(e) => handleSettingChange('reminderTime', e?.target?.value)}
                      className="
                        px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-border bg-background
                        text-foreground text-sm sm:text-base
                        focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring
                        transition-smooth
                      "
                    />
                  </div>

                  <div className="border-t border-border pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="block text-sm font-medium text-foreground">
                          Email Reminders
                        </label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Receive check-in reminders via email
                        </p>
                      </div>
                      <button
                        onClick={() => handleSettingChange('emailRemindersEnabled', !settings?.emailRemindersEnabled)}
                        className={`
                          relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full
                          transition-smooth focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2
                          ${settings?.emailRemindersEnabled ? 'bg-primary' : 'bg-muted'}
                        `}
                        role="switch"
                        aria-checked={settings?.emailRemindersEnabled}
                      >
                        <span
                          className={`
                            inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white
                            transition-smooth shadow-therapeutic
                            ${settings?.emailRemindersEnabled ? 'translate-x-6 sm:translate-x-6' : 'translate-x-1'}
                          `}
                        />
                      </button>
                    </div>

                    {settings?.emailRemindersEnabled && (
                      <div className="space-y-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Email Frequency
                          </label>
                          <select
                            value={settings?.emailReminderFrequency}
                            onChange={(e) => handleSettingChange('emailReminderFrequency', e?.target?.value)}
                            className="
                              w-full px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-border bg-background
                              text-foreground text-sm sm:text-base
                              focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring
                              transition-smooth cursor-pointer
                            "
                          >
                            {emailFrequencyOptions?.map((option) => (
                              <option key={option?.value} value={option?.value}>
                                {option?.label}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground mt-2">
                            {settings?.emailReminderFrequency === 'daily' && 'Receive a daily summary of your check-ins'}
                            {settings?.emailReminderFrequency === 'weekly' && 'Receive a weekly digest of your check-ins'}
                            {settings?.emailReminderFrequency === 'custom' && 'Select specific days and times for reminders'}
                          </p>
                        </div>

                        {settings?.emailReminderFrequency === 'custom' && (
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              Select Days
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {dayOptions?.map((day) => (
                                <button
                                  key={day?.value}
                                  onClick={() => toggleDay(day?.value)}
                                  className={`
                                    px-3 py-2 rounded-lg text-sm font-medium transition-smooth
                                    focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
                                    ${settings?.emailReminderDays?.includes(day?.value)
                                      ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                    }
                                  `}
                                >
                                  {day?.label?.substring(0, 3)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="bg-muted/50 rounded-lg p-3 sm:p-4">
                          <div className="flex items-start gap-2">
                            <Icon name="Info" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs sm:text-sm text-muted-foreground">
                                Email reminders help you maintain consistent symptom tracking for better health outcomes.
                              </p>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={handleTestEmail}
                          disabled={testEmailSending}
                          className="
                            w-full px-4 py-2.5 rounded-lg border-2 border-primary text-primary
                            font-medium text-sm transition-smooth
                            hover:bg-primary hover:text-white
                            focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2
                            disabled:opacity-50 disabled:cursor-not-allowed
                          "
                        >
                          {testEmailSending ? 'Sending...' : 'Send Test Email'}
                        </button>

                        {testEmailStatus && (
                          <div className={`
                            p-3 rounded-lg text-sm
                            ${testEmailStatus?.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}
                          `}>
                            {testEmailStatus?.message}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </SettingsSection>

            <SettingsSection
              title="Caregiver Alert Preferences"
              description="Control how you receive alerts about your dependents"
              icon="Users"
              collapsible={true}
              defaultOpen={false}
            >
              <SettingItem
                icon="Users"
                label="Enable Caregiver Alerts"
                description="Receive notifications when dependents log concerning symptoms"
                type="toggle"
                value={settings?.caregiverAlertEnabled}
                onChange={(value) => handleSettingChange('caregiverAlertEnabled', value)}
              />

              {settings?.caregiverAlertEnabled && (
                <div className="pl-0 sm:pl-4 space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Alert Frequency
                    </label>
                    <select
                      value={settings?.caregiverAlertFrequency}
                      onChange={(e) => handleSettingChange('caregiverAlertFrequency', e?.target?.value)}
                      className="
                        w-full px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-border bg-background
                        text-foreground text-sm sm:text-base
                        focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring
                        transition-smooth cursor-pointer
                      "
                    >
                      {caregiverAlertFrequencyOptions?.map((option) => (
                        <option key={option?.value} value={option?.value}>
                          {option?.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground mt-2">
                      {settings?.caregiverAlertFrequency === 'immediate' && 'Get notified instantly when alerts are triggered'}
                      {settings?.caregiverAlertFrequency === 'hourly_digest' && 'Receive a summary of alerts every hour'}
                      {settings?.caregiverAlertFrequency === 'daily_digest' && 'Receive a daily summary of all alerts'}
                      {settings?.caregiverAlertFrequency === 'critical_only' && 'Only receive notifications for critical severity alerts'}
                    </p>
                  </div>

                  <div className="border-t border-border pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="block text-sm font-medium text-foreground">
                          Quiet Hours
                        </label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Pause non-critical alerts during specific hours
                        </p>
                      </div>
                      <button
                        onClick={() => handleSettingChange('caregiverAlertQuietHours', {
                          ...settings?.caregiverAlertQuietHours,
                          enabled: !settings?.caregiverAlertQuietHours?.enabled
                        })}
                        className={`
                          relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full
                          transition-smooth focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2
                          ${settings?.caregiverAlertQuietHours?.enabled ? 'bg-primary' : 'bg-muted'}
                        `}
                        role="switch"
                        aria-checked={settings?.caregiverAlertQuietHours?.enabled}
                      >
                        <span
                          className={`
                            inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white
                            transition-smooth shadow-therapeutic
                            ${settings?.caregiverAlertQuietHours?.enabled ? 'translate-x-6 sm:translate-x-6' : 'translate-x-1'}
                          `}
                        />
                      </button>
                    </div>

                    {settings?.caregiverAlertQuietHours?.enabled && (
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Start Time
                          </label>
                          <input
                            type="time"
                            value={settings?.caregiverAlertQuietHours?.start}
                            onChange={(e) => handleSettingChange('caregiverAlertQuietHours', {
                              ...settings?.caregiverAlertQuietHours,
                              start: e?.target?.value
                            })}
                            className="
                              w-full px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-border bg-background
                              text-foreground text-sm sm:text-base
                              focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring
                              transition-smooth
                            "
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            End Time
                          </label>
                          <input
                            type="time"
                            value={settings?.caregiverAlertQuietHours?.end}
                            onChange={(e) => handleSettingChange('caregiverAlertQuietHours', {
                              ...settings?.caregiverAlertQuietHours,
                              end: e?.target?.value
                            })}
                            className="
                              w-full px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-border bg-background
                              text-foreground text-sm sm:text-base
                              focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring
                              transition-smooth
                            "
                          />
                        </div>
                      </div>
                    )}

                    <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 flex items-start gap-3">
                      <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" strokeWidth={2} />
                      <p className="text-sm text-foreground">
                        <strong>Note:</strong> Critical severity alerts will always be delivered immediately, regardless of frequency or quiet hours settings.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </SettingsSection>

            <SettingsSection
              title="Support Email Notifications"
              description="Manage how you receive support and system emails"
              icon="Mail"
              collapsible={true}
              defaultOpen={false}
            >
              <SettingItem
                icon="Mail"
                label="Enable Support Emails"
                description="Receive email notifications for support responses and updates"
                type="toggle"
                value={settings?.supportEmailEnabled}
                onChange={(value) => handleSettingChange('supportEmailEnabled', value)}
              />

              {settings?.supportEmailEnabled && (
                <div className="pl-0 sm:pl-4 space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Email Frequency
                    </label>
                    <select
                      value={settings?.supportEmailFrequency}
                      onChange={(e) => handleSettingChange('supportEmailFrequency', e?.target?.value)}
                      className="
                        w-full px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-border bg-background
                        text-foreground text-sm sm:text-base
                        focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring
                        transition-smooth cursor-pointer
                      "
                    >
                      {supportEmailFrequencyOptions?.map((option) => (
                        <option key={option?.value} value={option?.value}>
                          {option?.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground mt-2">
                      {settings?.supportEmailFrequency === 'immediate' && 'Receive support emails as soon as they are sent'}
                      {settings?.supportEmailFrequency === 'daily_digest' && 'Receive a daily summary of support communications'}
                      {settings?.supportEmailFrequency === 'weekly_digest' && 'Receive a weekly summary of support communications'}
                    </p>
                  </div>

                  <div className="bg-info/10 border border-info/20 rounded-lg p-4 flex items-start gap-3">
                    <Icon name="Info" size={20} className="text-info flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <div>
                      <p className="text-sm text-foreground font-medium mb-1">
                        What's included:
                      </p>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Support ticket responses</li>
                        <li>System maintenance notifications</li>
                        <li>Feature updates and announcements</li>
                        <li>Account security alerts</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </SettingsSection>

            <SettingsSection
              title="Data Management"
              description="Export or clear your stored data"
              icon="Database"
            >
              <DataManagementCard
                onClearData={handleClearData}
                onExportData={handleExportData}
              />
            </SettingsSection>

            <SettingsSection
              title="Help & Support"
              description="Get assistance and learn more"
              icon="HelpCircle"
            >
              <HelpSupportSection />
            </SettingsSection>

            {/* Privacy & Legal Section */}
            <SettingsSection
              title="Privacy & Legal"
              description="Review our policies and your data rights"
              icon="FileText"
              collapsible={true}
              defaultOpen={false}
            >
              <SettingItem
                icon="ShieldCheck"
                label="GDPR Compliance & Security"
                description="View GDPR compliance, AES-256 encryption, ISO 27001/SOC 2 certifications, and security audits"
                type="button"
                buttonText="View Details"
                onButtonClick={() => navigate('/gdpr-compliance-security')}
                value={null}
                onChange={() => {}}
                min={0}
                max={100}
              />
              <SettingItem
                icon="Shield"
                label="Your GDPR Rights"
                description="Learn about your data protection rights and how to exercise them"
                type="button"
                buttonText="View Rights"
                onButtonClick={() => navigate('/gdpr-rights')}
                value={null}
                onChange={() => {}}
                min={0}
                max={100}
              />
              <SettingItem
                icon="FileText"
                label="Privacy Policy"
                description="Read our privacy policy and data handling practices"
                type="button"
                buttonText="View Policy"
                onButtonClick={() => navigate('/privacy-policy')}
                value={null}
                onChange={() => {}}
                min={0}
                max={100}
              />
              <SettingItem
                icon="FileText"
                label="Terms & Conditions"
                description="Review our terms of service and user agreement"
                type="button"
                buttonText="View Terms"
                onButtonClick={() => navigate('/terms-and-conditions')}
                value={null}
                onChange={() => {}}
                min={0}
                max={100}
              />
            </SettingsSection>

            {/* Officer-Only Analytics Sections */}
            {userProfile?.user_type === 'caregiver' && (
              <SettingsSection
                title="Business Intelligence & Analytics"
                description="Access advanced analytics and utilization metrics"
                icon="TrendingUp"
              >
                <div className="space-y-3">
                  <RestrictedFeature
                    hasAccess={hasBusinessIntelligenceAccess}
                    feature={OFFICER_FEATURES?.BUSINESS_INTELLIGENCE}
                    showWarning={true}
                    warningMessage="Business Intelligence Dashboard requires officer permissions"
                    onClick={() => navigate('/business-intelligence-dashboard')}
                  >
                    <button
                      onClick={() => navigate('/business-intelligence-dashboard')}
                      className="
                        w-full flex items-start gap-3 sm:gap-4 p-4 sm:p-5 rounded-lg
                        bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200
                        hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300 hover:shadow-lg
                        transition-smooth text-left
                        focus:outline-none focus:ring-3 focus:ring-blue-400
                        active:scale-[0.98]
                      "
                      aria-label="Open Business Intelligence Dashboard"
                    >
                      <div className="flex items-center justify-center w-12 h-12 bg-blue-600 rounded-lg flex-shrink-0">
                        <Icon name="TrendingUp" size={24} color="white" strokeWidth={2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Master Business Intelligence Dashboard
                        </h3>
                        <p className="text-sm text-gray-700 mb-2">
                          View comprehensive utilization metrics, user engagement analytics, premium conversion funnels, cohort retention, revenue analytics, and optimization strategies.
                        </p>
                        <div className="flex flex-wrap gap-2 mb-2">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-white/80 rounded text-xs font-medium text-blue-700">
                            <Icon name="Users" size={12} />
                            User Segmentation
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-white/80 rounded text-xs font-medium text-blue-700">
                            <Icon name="DollarSign" size={12} />
                            Revenue Analytics
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-white/80 rounded text-xs font-medium text-blue-700">
                            <Icon name="Target" size={12} />
                            Conversion Tracking
                          </span>
                        </div>
                        <span className="text-sm text-blue-600 font-semibold inline-flex items-center gap-1">
                          Open Dashboard
                          <Icon name="ArrowRight" size={16} strokeWidth={2} />
                        </span>
                      </div>
                    </button>
                  </RestrictedFeature>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <RestrictedFeature
                      hasAccess={hasUserEngagementAccess}
                      feature={OFFICER_FEATURES?.USER_ENGAGEMENT_ANALYTICS}
                      showWarning={true}
                      warningMessage="User Engagement Analytics requires officer permissions"
                      onClick={() => navigate('/user-engagement-analytics')}
                    >
                      <button
                        onClick={() => navigate('/user-engagement-analytics')}
                        className="
                          flex items-start gap-3 p-4 rounded-lg
                          bg-muted/50 border border-border
                          hover:bg-muted hover:border-primary/30 hover:shadow-therapeutic
                          transition-smooth text-left
                          focus:outline-none focus:ring-3 focus:ring-ring
                          active:scale-[0.98]
                        "
                        aria-label="Open User Engagement Analytics"
                      >
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 flex-shrink-0">
                          <Icon name="Activity" size={20} color="var(--color-primary)" strokeWidth={2} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-foreground mb-1">
                            User Engagement
                          </h4>
                          <p className="text-xs text-muted-foreground mb-2">
                            Track engagement scores, user journeys, and feature adoption
                          </p>
                          <span className="text-xs text-primary font-medium inline-flex items-center gap-1">
                            View Analytics
                            <Icon name="ArrowRight" size={12} strokeWidth={2} />
                          </span>
                        </div>
                      </button>
                    </RestrictedFeature>

                    <RestrictedFeature
                      hasAccess={hasConversionFunnelAccess}
                      feature={OFFICER_FEATURES?.CONVERSION_FUNNEL_ANALYTICS}
                      showWarning={true}
                      warningMessage="Conversion Funnel Analytics requires officer permissions"
                      onClick={() => navigate('/premium-conversion-funnel-analytics')}
                    >
                      <button
                        onClick={() => navigate('/premium-conversion-funnel-analytics')}
                        className="
                          flex items-start gap-3 p-4 rounded-lg
                          bg-muted/50 border border-border
                          hover:bg-muted hover:border-primary/30 hover:shadow-therapeutic
                          transition-smooth text-left
                          focus:outline-none focus:ring-3 focus:ring-ring
                          active:scale-[0.98]
                        "
                        aria-label="Open Premium Conversion Analytics"
                      >
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 flex-shrink-0">
                          <Icon name="Target" size={20} color="var(--color-primary)" strokeWidth={2} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-foreground mb-1">
                            Conversion Funnel
                          </h4>
                          <p className="text-xs text-muted-foreground mb-2">
                            Analyze premium conversion rates and optimization opportunities
                          </p>
                          <span className="text-xs text-primary font-medium inline-flex items-center gap-1">
                            View Funnel
                            <Icon name="ArrowRight" size={12} strokeWidth={2} />
                          </span>
                        </div>
                      </button>
                    </RestrictedFeature>
                  </div>

                  {!hasBusinessIntelligenceAccess && (
                    <PermissionWarning 
                      message="Business Intelligence and Analytics dashboards are restricted to MyBodyToday officers only."
                      variant="default"
                    />
                  )}

                  {hasBusinessIntelligenceAccess && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600 flex-shrink-0">
                          <Icon name="Info" size={16} color="white" strokeWidth={2} />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-blue-900 mb-1">Owner Access</h4>
                          <p className="text-xs text-blue-700">
                            These analytics dashboards are available to caregiver accounts for monitoring platform utilization, user engagement, and business metrics. Use these tools to track KPIs, identify trends, and optimize user experience.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </SettingsSection>
            )}

            <SettingsSection
              title="Privacy Information"
              description="Learn how we protect your data"
              icon="ShieldCheck"
            >
              <PrivacyInfoCard />
            </SettingsSection>

            {/* Security Section */}
            <SettingsSection title="Security" description="Manage your account security settings" icon="Shield">
              <SettingItem
                icon="Shield"
                label="Two-Factor Authentication"
                description="Add an extra layer of security"
                type="button"
                buttonText="Configure"
                onButtonClick={() => navigate('/two-factor-authentication-setup')}
                value={null}
                onChange={() => {}}
                min={0}
                max={100}
              />
              <SettingItem
                icon="Monitor"
                label="Active Sessions"
                description={`${sessions?.length || 0} active devices`}
                type="button"
                buttonText="Manage"
                onButtonClick={() => navigate('/session-management')}
                value={null}
                onChange={() => {}}
                min={0}
                max={100}
              />
            </SettingsSection>

            {/* Enhanced Privacy Section */}
            {privacySettings && (
              <SettingsSection title="Privacy Controls" icon="Lock" description="Control your privacy settings">
                <SettingItem
                  icon="Eye"
                  label="Symptom Data Visibility"
                  description="Control who can see your symptom data"
                  type="select"
                  value={privacySettings?.symptom_data_visibility}
                  options={[
                    { value: 'private', label: 'Private (Only Me)' },
                    { value: 'caregivers_only', label: 'Caregivers Only' },
                    { value: 'public', label: 'Public' }
                  ]}
                  onChange={(value) => handlePrivacySettingChange('symptom_data_visibility', value)}
                />
                <SettingItem
                  icon="MapPin"
                  label="Location Tracking"
                  description="Allow location-based insights"
                  type="toggle"
                  value={privacySettings?.location_tracking_enabled}
                  onChange={(value) => handlePrivacySettingChange('location_tracking_enabled', value)}
                />
                <SettingItem
                  icon="Share2"
                  label="Third-Party Sharing"
                  description="Share anonymized data for research"
                  type="toggle"
                  value={privacySettings?.third_party_sharing_enabled}
                  onChange={(value) => handlePrivacySettingChange('third_party_sharing_enabled', value)}
                />
              </SettingsSection>
            )}

            {hasAuditTrailAccess && (
              <div className="bg-card rounded-xl border border-border p-4 sm:p-6">
                <h2 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Shield" size={20} strokeWidth={2} />
                  Data Integrity & Audit
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Track all modifications to your symptom data with comprehensive audit trails showing who accessed or modified your information, when changes occurred, and what was changed.
                </p>
                
                <button
                  onClick={() => navigate('/audit-trail-dashboard')}
                  className="w-full flex items-center justify-between px-4 py-3 bg-background border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  <span className="text-sm font-medium text-foreground">View Audit Trail</span>
                  <Icon name="ChevronRight" size={20} strokeWidth={2} className="text-muted-foreground" />
                </button>
              </div>
            )}

            {/* Danger Zone Section */}
            <div className="mb-24">
              <SettingsSection
                icon="AlertTriangle"
                title="Danger Zone"
                description="Irreversible actions that permanently affect your account"
              >
                {/* Data Export Before Deletion */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Icon name="Download" size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-blue-900 mb-2">
                        Export Your Data
                      </h3>
                      <p className="text-sm text-blue-700 mb-4">
                        Download all your symptom data, profile information, and settings before deleting your account. This ensures you have a backup of your health records.
                      </p>
                      {exportSuccess && (
                        <div className="flex items-center gap-2 text-success mb-3">
                          <Icon name="CheckCircle" size={18} color="var(--color-success)" />
                          <span className="text-sm font-medium">Data exported successfully!</span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <Button
                          variant="outline"
                          onClick={handleExportJSON}
                          loading={exportLoading}
                          iconName="FileJson"
                          size="sm"
                        >
                          Export as JSON
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleExportCSV}
                          loading={exportLoading}
                          iconName="FileText"
                          size="sm"
                        >
                          Export as CSV
                        </Button>
                      </div>
                      <p className="text-xs text-blue-600 mt-3">
                        <strong>JSON:</strong> Complete data export including all fields and metadata<br />
                        <strong>CSV:</strong> Symptom entries in spreadsheet format for analysis
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-red-900 mb-2">
                        Delete Account
                      </h3>
                      <p className="text-sm text-red-700 mb-4">
                        Permanently delete your account and all associated data. This action cannot be undone.
                      </p>
                      <ul className="text-xs text-red-600 space-y-1 mb-4">
                        <li>• All symptom entries will be permanently deleted</li>
                        <li>• Your profile and personal information will be removed</li>
                        <li>• Caregiver relationships will be terminated</li>
                        <li>• Active subscriptions will be canceled</li>
                        <li>• You will receive a confirmation email with recovery information</li>
                        <li>• 30-day recovery window before permanent deletion</li>
                      </ul>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={() => setShowDeleteDialog(true)}
                      iconName="Trash2"
                      size="sm"
                    >
                      Delete Account
                    </Button>
                  </div>
                </div>
              </SettingsSection>
            </div>

            {/* Delete Account Confirmation Dialog */}
            {showDeleteDialog && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={(e) => {
                if (e?.target === e?.currentTarget && !deleteLoading) {
                  setShowDeleteDialog(false);
                  setDeletePassword('');
                  setDeleteError('');
                }
              }}>
                <div className="bg-card rounded-lg shadow-xl max-w-md w-full p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <Icon name="AlertTriangle" size={20} className="text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1">
                        Delete Account?
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        This will permanently delete your account and all data.
                      </p>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-800 font-medium mb-2">
                      The following will be permanently deleted:
                    </p>
                    <ul className="text-xs text-red-700 space-y-1">
                      <li>✗ All symptom entries and health data</li>
                      <li>✗ Your profile and personal information</li>
                      <li>✗ Managed profiles and caregiver relationships</li>
                      <li>✗ Subscription and billing history</li>
                      <li>✗ All photos and voice recordings</li>
                      <li>✗ Medication tracking data</li>
                    </ul>
                  </div>

                  {deleteError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-red-800">{deleteError}</p>
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Enter your password to confirm
                    </label>
                    <Input
                      type="password"
                      value={deletePassword}
                      onChange={(e) => {
                        setDeletePassword(e?.target?.value);
                        setDeleteError('');
                      }}
                      placeholder="Enter your password"
                      disabled={deleteLoading}
                      className="min-h-[44px]"
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowDeleteDialog(false);
                        setDeletePassword('');
                        setDeleteError('');
                      }}
                      disabled={deleteLoading}
                      fullWidth
                      className="min-h-[44px] touch-manipulation active:scale-95"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteAccount}
                      loading={deleteLoading}
                      disabled={!deletePassword || deleteLoading}
                      fullWidth
                      className="min-h-[44px] touch-manipulation active:scale-95"
                    >
                      Delete Forever
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-4 sm:pt-6 pb-2">
              <div className="text-center text-xs sm:text-sm text-muted-foreground">
                <p>MyBodyToday v1.0.0</p>
                <p className="mt-1">&copy; {new Date()?.getFullYear()} MyBodyToday. All rights reserved.</p>
              </div>
            </div>
          </div>
        </main>

        <BottomTabNavigation />
      </div>
    </>
  );
};

export default SettingsAndPrivacy;